import React, { useEffect, useMemo, useState } from 'react'
import { DefaultLink, FjTable, InternalUseTag, ThreeDotMenuButton } from 'src/components/Common'
import { TablePaginationConfig, Tooltip } from 'antd'
import { LibraryCategory } from 'src/pages/Library'
import { getFeedContentTargetLink, getFeedContentTitle, LearningContent } from 'src/utils/content'
import { AuthorDisplay } from 'src/components/Common/AuthorDisplay'
import { Asset } from 'src/models/Asset'
import { formatDateTime, truncate } from 'src/utils/format'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { ColumnsType } from 'antd/lib/table'
import { TableRowSelection } from 'antd/lib/table/interface'
import { sharedDataStore } from 'src/store/DataStore'
import { ContentTag } from 'src/components/Feed/ContentTag'
import { LibraryStats } from 'src/components/Library/LibraryStats'
import { ContentMenu } from 'src/components/Feed/ContentMenu'

const DEFAULT_PAGE_SIZE = 15

export const TABLE_DATE_FORMAT = 'MMM DD, YYYY'

/**
 We don't paginate search results properly but we do figure out if there are more results to be loaded
 based on how many results are in the most recent page (i.e. if results.length < pageSize we know there are no more results)
 In order for table pagination to work properly we need to give a total number of items so we calculate that dynamically
 so the user doesn't see 1 of 999999 items in 60k pages or whatever
 */
const calculateTotal = (
  contentType: LibraryCategory,
  hasMore: boolean,
  page: number,
  pageSize: number,
  total: number
) => {
  if (!contentType.includes('search')) return total
  return page * pageSize + (hasMore ? 1 : 0)
}

interface ILibraryTableViewProps {
  loading: boolean
  contentType: LibraryCategory
  learningContents: LearningContent[]
  pageNumber: number
  total: number
  hasMore: boolean
  onEditSuccess: (objs: LearningContent[]) => void
  onArchiveSuccess: (objs: LearningContent[]) => void
  handlePageChange: (pageNumber: number) => void
  onDeleteSuccess: (objs: LearningContent[]) => void
}

export const LibraryTableView: React.FC<ILibraryTableViewProps> = ({
  loading,
  learningContents,
  total,
  pageNumber,
  contentType,
  hasMore,
  onEditSuccess,
  onArchiveSuccess,
  onDeleteSuccess,
  handlePageChange,
}) => {
  const [selectedRows, setSelectedRows] = useState<LearningContent[]>([])
  const paginationSettings = useMemo<TablePaginationConfig>(() => {
    return {
      showSizeChanger: false,
      pageSize: DEFAULT_PAGE_SIZE,
      onChange: (page: number) => {
        // things get hard to maintain if we try to keep selection while paging
        sharedAppStateStore.bulkEditPopupProps = undefined
        handlePageChange(page)
      },
      showTotal: !contentType.includes('search')
        ? (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`
        : undefined,
      current: pageNumber,
      total: calculateTotal(contentType, hasMore, pageNumber, DEFAULT_PAGE_SIZE, total),
      simple: sharedAppStateStore.isMobile,
    }
  }, [contentType, hasMore, total, pageNumber, handlePageChange])

  const showAuthor = useMemo(
    () => !['coachingMoment', 'task', 'opportunity', 'call'].includes(contentType),
    [contentType]
  )

  const showContentTypeIcon = useMemo(
    () => ['top', 'managerPicks', 'favourites', 'fullsearch', 'aisearch'].includes(contentType),
    [contentType]
  )

  const columns: ColumnsType<LearningContent> = useMemo(() => {
    return [
      {
        title: 'Title',
        key: 'title',
        render: (obj: LearningContent) => {
          const truncateLength = 50
          const title = getFeedContentTitle(obj)
          const truncateTitle = title.length > truncateLength
          const isAsset = obj instanceof Asset
          return (
            <DefaultLink
              color="linkBlue"
              to={getFeedContentTargetLink(obj)}
              style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {showContentTypeIcon ? <ContentTag obj={obj} showTagTitle={false} /> : null}
                <Tooltip title={truncateTitle ? title : null}>
                  {truncateTitle ? truncate(title, truncateLength) : title}
                </Tooltip>
                {isAsset && obj.isInternal ? <InternalUseTag style={{ marginLeft: '8px' }} /> : null}
              </span>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LibraryStats obj={obj} containerStyles={{ bottom: undefined, position: undefined }} />
              </div>
            </DefaultLink>
          )
        },
      },
      showAuthor
        ? {
            title: 'Author',
            key: 'author',
            render: (obj: LearningContent) =>
              obj.author ? <AuthorDisplay author={(obj as any).author} showName={false} /> : null,
          }
        : null,
      contentType === 'asset'
        ? {
            title: 'Asset Type',
            key: 'assetType',
            render: (obj: LearningContent) => (obj as Asset).fileTypeDisplay(),
          }
        : null,
      {
        title: 'Last Updated',
        key: 'lastUpdated',
        render: (obj: LearningContent) =>
          obj['lastUpdated'] ? formatDateTime((obj as any).lastUpdated, TABLE_DATE_FORMAT) : null,
      },
      {
        title: 'Expiry Date',
        key: 'expiryDate',
        render: (obj: LearningContent) =>
          obj['expiryDate'] ? formatDateTime((obj as any).expiryDate, TABLE_DATE_FORMAT) : null,
      },
      {
        title: 'Actions',
        key: 'actions',
        fixed: 'right' as any,
        render: (lc: LearningContent) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ContentMenu
              obj={lc}
              onEditSuccess={(obj) => onEditSuccess([obj as LearningContent])}
              onArchiveSuccess={() => onArchiveSuccess([lc])}
              onDeleteSuccess={() => onDeleteSuccess([lc])}
            >
              <ThreeDotMenuButton />
            </ContentMenu>
          </div>
        ),
      },
    ].filter(Boolean)
  }, [contentType, onEditSuccess, onArchiveSuccess, onDeleteSuccess, showAuthor, showContentTypeIcon])

  const rowSelection: TableRowSelection<LearningContent> = useMemo(() => {
    if (!sharedDataStore.user.isFaasAdmin() || !showAuthor) return
    const clearSelection = () => {
      setSelectedRows([])
      sharedAppStateStore.bulkEditPopupProps = undefined
    }

    const handleEditSuccess = (objs: LearningContent[]) => {
      onEditSuccess(objs)
      clearSelection()
    }

    return {
      selectedRowKeys: selectedRows.map((obj) => obj.id),
      onChange: (_, newSelectedRows) => {
        setSelectedRows(newSelectedRows)
        if (newSelectedRows.length) {
          sharedAppStateStore.bulkEditPopupProps = {
            learningContents: [...newSelectedRows],
            onEditSuccess: handleEditSuccess,
            onArchiveSuccess,
            onDeleteSuccess,
            clearSelection,
          }
        } else {
          sharedAppStateStore.bulkEditPopupProps = undefined
        }
      },
    }
  }, [showAuthor, selectedRows, setSelectedRows, onEditSuccess, onArchiveSuccess, onDeleteSuccess])

  useEffect(() => {
    sharedAppStateStore.bulkEditPopupProps = undefined
  }, [contentType])

  return (
    <div style={{ marginBottom: '80px' }}>
      <FjTable
        loading={loading}
        rowKey={(obj) => obj.id}
        columns={columns}
        dataSource={learningContents}
        pagination={paginationSettings}
        rowSelection={rowSelection}
      />
    </div>
  )
}
